import type { WireLine } from '@/api/client';

// Columns a reader can add between the module and the message, each a field of the query language: a click on a cell keeps
// its value, ⌥-click excludes it. The URL carries them (`cols=status,user`), and so will saved views.
export interface ColumnDef {
    key: string;
    label: string;
    width: string;
    // The query language's field, for filtering by a cell.
    field: string;
    value: (line: WireLine) => string;
    // What a click filters by, when not the value shown (the whole commit); null: the cell does not filter.
    filterValue?: (line: WireLine) => string | null;
    // A person's id: hidden in privacy mode.
    secret?: boolean;
}

export const EXTRA_COLUMNS: readonly ColumnDef[] = [
    { key: 'status', label: 'Status', width: '48px', field: 'status', value: (line) => (line.http_status ? String(line.http_status) : '') },
    {
        key: 'duration',
        label: 'Duration',
        width: '72px',
        field: 'duration',
        value: (line) => (line.duration_ms ? `${Math.round(line.duration_ms)} ms` : ''),
        filterValue: () => null,
    },
    { key: 'method', label: 'Method', width: '56px', field: 'method', value: (line) => line.http_method },
    { key: 'path', label: 'Path', width: '200px', field: 'path', value: (line) => line.http_path },
    { key: 'user', label: 'User', width: '92px', field: 'user', value: (line) => line.user_id, secret: true },
    { key: 'request', label: 'Request', width: '120px', field: 'request', value: (line) => line.request_id },
    { key: 'instance', label: 'Instance', width: '116px', field: 'instance', value: (line) => line.instance },
    {
        key: 'commit',
        label: 'Commit',
        width: '64px',
        field: 'commit',
        value: (line) => line.commit.slice(0, 7),
        filterValue: (line) => line.commit,
    },
    { key: 'exception', label: 'Exception', width: '140px', field: 'exception', value: (line) => line.exception },
    { key: 'error_uuid', label: 'Exception id', width: '112px', field: 'error_uuid', value: (line) => line.error_uuid },
];

const BY_KEY = new Map(EXTRA_COLUMNS.map((column) => [column.key, column]));

export const columnsFrom = (keys: readonly string[]): ColumnDef[] =>
    keys.flatMap((key) => {
        const column = BY_KEY.get(key);

        return column ? [column] : [];
    });

// The grid of the header and of every row: time, level, service, module, the extra columns, the message. A narrow
// list (the inspector open, a small window) gives the module's room to the message.
export const gridTemplate = (columns: readonly ColumnDef[], compact = false): string =>
    ['104px', '46px', '66px', compact ? '0px' : '136px', ...columns.map((column) => column.width), 'minmax(0, 1fr)'].join(' ');
